import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { useStock } from "@/context/StockContext";
import { Minus, Plus, Trash2, Tag, X, Send, ArrowRight, ArrowLeft } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { CartNudges } from "@/components/CartNudges";
import { money, FREE_SHIP_THRESHOLD } from "@/data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartLine = ({ i, soldOut, updateQty, removeItem }) => (
  <div className="flex gap-4 border-b border-zinc-800 pb-4" data-testid={`cart-item-${i.id}`}>
    <img src={i.image} alt={i.name} className={`w-16 h-16 object-cover bg-black ${soldOut ? "opacity-40 grayscale" : ""}`} />
    <div className="flex-1 min-w-0">
      <p className="font-serif text-lg leading-tight truncate">{i.name}</p>
      {soldOut && <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-red-400 mt-0.5" data-testid={`cart-soldout-${i.id}`}>Sold out — remove to check out</p>}
      <p className="font-mono text-[11px] text-[#D4AF37]">
        {i.size} · {i.saved > 0 ? (<span><span className="line-through text-[#52525B]">${i.price}</span> ${i.unit}</span>) : (<span>${i.price}</span>)}
      </p>
      {i.tier && <p className="font-mono text-[9px] uppercase tracking-wide text-emerald-400 mt-0.5">{i.tier.label}</p>}
      <div className="flex items-center gap-3 mt-2">
        <button onClick={() => updateQty(i.id, i.qty - 1)} className="w-6 h-6 border border-zinc-700 flex items-center justify-center hover:border-[#D4AF37]" data-testid={`cart-dec-${i.id}`}><Minus size={12} /></button>
        <span className="font-mono text-sm w-4 text-center">{i.qty}</span>
        <button onClick={() => updateQty(i.id, i.qty + 1)} className="w-6 h-6 border border-zinc-700 flex items-center justify-center hover:border-[#D4AF37]" data-testid={`cart-inc-${i.id}`}><Plus size={12} /></button>
        <button onClick={() => removeItem(i.id)} className="ml-auto text-[#52525B] hover:text-red-500" data-testid={`cart-remove-${i.id}`}><Trash2 size={15} /></button>
      </div>
    </div>
  </div>
);

export default function CartSheet() {
  const navigate = useNavigate();
  const { lines, open, setOpen, updateQty, removeItem, subtotal, bulkSavings, promo, promoDiscount, shipping, freeShip, freeShipRemaining, bundleHint, total, note, setNote, applyPromo, removePromo } = useCart();
  const { isSoldOut, refresh } = useStock();
  const soldOutLines = lines.filter((l) => isSoldOut(l));
  const hasSoldOut = soldOutLines.length > 0;
  const [code, setCode] = useState("");
  const [saveEmail, setSaveEmail] = useState(localStorage.getItem("mx_email") || "");

  // Refresh live stock whenever the cart opens so sold-out flags are current.
  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  // Watchdog: Radix portals can leave pointer-events:none stuck on <body> after
  // rapid open/close (freezes the page). Always restore interactivity on close.
  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        if (document.body.style.pointerEvents === "none") document.body.style.pointerEvents = "";
      }, 400);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  const emailCart = async () => {
    if (!/^[^@]+@[^@]+\.[^@]+$/.test(saveEmail)) { toast.error("Enter a valid email"); return; }
    try {
      await axios.post(`${API}/carts/email`, {
        email: saveEmail,
        items: lines.map((l) => ({ id: l.id, name: l.name, size: l.size, qty: l.qty, unit: l.unit, lineTotal: l.lineTotal })),
        total,
      });
      localStorage.setItem("mx_email", saveEmail);
      toast.success("Cart emailed — finish anytime, any device.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not email cart"); }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="bg-[#0C0C0C] border-l border-zinc-800 text-[#F5F5F0] w-full sm:max-w-md flex flex-col rounded-none" data-testid="cart-sheet">
        <SheetHeader>
          <SheetTitle className="font-serif text-3xl font-light text-left text-[#F5F5F0]">Your Order</SheetTitle>
          <SheetDescription className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#52525B] text-left">Research use only · buy 5+ to save</SheetDescription>
        </SheetHeader>
        <button onClick={() => setOpen(false)} data-testid="cart-continue-shopping"
          className="mt-3 w-full flex items-center justify-center gap-2 border border-zinc-700 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
          <ArrowLeft size={13} /> Continue Shopping
        </button>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 mt-6 space-y-4">
          {lines.length === 0 && <p className="text-[#52525B] font-mono text-sm mt-10" data-testid="cart-empty">Your cart is empty.</p>}
          {lines.map((i) => (
            <CartLine key={i.id} i={i} soldOut={isSoldOut(i)} updateQty={updateQty} removeItem={removeItem} />
          ))}
        </div>

        <div className="border-t border-zinc-800 pt-5 mt-4">
          {lines.length > 0 && <CartNudges />}
          {/* Order note */}
          {lines.length > 0 && (
            <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="order-note"
              rows={2} placeholder="Add a note to your order (optional)"
              className="w-full bg-transparent border border-zinc-700 px-3 py-2.5 mb-3 font-mono text-xs outline-none focus:border-[#D4AF37] placeholder:text-[#52525B] resize-none transition-colors" />
          )}
          {lines.length > 0 && (
            <div className="flex gap-2 mb-4">
              <input value={saveEmail} onChange={(e) => setSaveEmail(e.target.value)} type="email" placeholder="Email my cart" data-testid="save-cart-input"
                className="flex-1 bg-transparent border border-zinc-700 px-3 py-2.5 font-mono text-xs outline-none focus:border-[#D4AF37] placeholder:text-[#52525B] transition-colors" />
              <button onClick={emailCart} data-testid="save-cart-btn"
                className="flex items-center gap-1.5 border border-zinc-700 px-4 font-mono text-[10px] uppercase tracking-[0.15em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
                <Send size={12} /> Save
              </button>
            </div>
          )}
          {/* Promo */}
          {promo ? (
            <div className="flex items-center justify-between border border-emerald-500/30 bg-emerald-500/5 px-4 py-3 mb-4" data-testid="promo-applied">
              <span className="flex items-center gap-2 font-mono text-xs text-emerald-400"><Tag size={13} /> {promo.code} · {promo.label || (promo.amountOff ? `$${promo.amountOff} store credit` : `${promo.percent}% off`)}</span>
              <button onClick={removePromo} data-testid="promo-remove" className="text-[#52525B] hover:text-white"><X size={15} /></button>
            </div>
          ) : (
            <div className="flex gap-2 mb-4">
              <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Promo code" data-testid="promo-input"
                className="flex-1 bg-transparent border border-zinc-700 px-3 py-2.5 font-mono text-xs uppercase tracking-wide outline-none focus:border-[#D4AF37] placeholder:text-[#52525B] transition-colors" />
              <button onClick={() => applyPromo(code)} data-testid="promo-apply"
                className="border border-zinc-700 px-4 font-mono text-[10px] uppercase tracking-[0.15em] hover:border-[#D4AF37] transition-colors">Apply</button>
            </div>
          )}

          {bundleHint && (
            <p className="font-mono text-[11px] text-[#D4AF37] mb-3" data-testid="bundle-hint">{bundleHint}</p>
          )}
          {(bulkSavings > 0 || promoDiscount > 0) && (
            <div className="flex justify-between items-baseline mb-2">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#52525B]">Subtotal</span>
              <span className="font-mono text-sm text-[#52525B] line-through">${subtotal}</span>
            </div>
          )}
          {bulkSavings > 0 && (
            <div className="flex justify-between items-baseline mb-1.5">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-emerald-400">Bulk savings</span>
              <span className="font-mono text-sm text-emerald-400" data-testid="cart-savings">-${bulkSavings}</span>
            </div>
          )}
          {promoDiscount > 0 && (
            <div className="flex justify-between items-baseline mb-1.5">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-emerald-400">{promo?.kind === "bundle" ? `Deal · ${promo.label}` : "Promo code"}</span>
              <span className="font-mono text-sm text-emerald-400" data-testid="cart-promo-discount">-${promoDiscount}</span>
            </div>
          )}
          {lines.length > 0 && (
            <div className="flex justify-between items-baseline mb-1.5" data-testid="cart-shipping-row">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA]">Shipping</span>
              <span className={`font-mono text-sm ${shipping > 0 ? "text-[#E8E8E3]" : "text-emerald-400"}`} data-testid="cart-shipping">{shipping > 0 ? money(shipping) : "Free"}</span>
            </div>
          )}
          {lines.length > 0 && !freeShip && (
            <p className="font-mono text-[10px] text-[#D4AF37] mb-3" data-testid="cart-freeship-nudge">
              Add {money(freeShipRemaining)} more to unlock free shipping (${FREE_SHIP_THRESHOLD}+).
            </p>
          )}
          <div className="flex justify-between items-baseline mb-5 mt-1">
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA]">Total</span>
            <span className="font-serif text-3xl" data-testid="cart-total">{money(total)}</span>
          </div>
          {hasSoldOut && (
            <p className="font-mono text-[11px] text-red-400 mb-3 text-center" data-testid="cart-soldout-warning">
              Remove sold-out item(s) to continue: {soldOutLines.map((l) => l.name).join(", ")}
            </p>
          )}
          <button onClick={() => { setOpen(false); navigate("/checkout"); }} disabled={!lines.length || hasSoldOut} data-testid="checkout-btn"
            className="w-full flex items-center justify-center gap-3 bg-[#D4AF37] text-black py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#C5A028] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            Proceed to Checkout <ArrowRight size={16} />
          </button>
          <p className="text-[#52525B] text-[10px] font-mono text-center mt-3">Free shipping on orders ${FREE_SHIP_THRESHOLD}+ · Secure card checkout.</p>
          <p className="text-[#D4AF37]/70 text-[10px] font-mono text-center mt-1.5" data-testid="cart-ruo-disclaimer">For Research &amp; Laboratory Use Only — not for human or veterinary use.</p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
